import { useCallback, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Grid, TextInput, Checkbox } from '@ts/storybook';
import { useTranslation } from 'react-i18next';
import { TMode, CStatusIndicator } from '@ts/ui-models';
import Misc from '@components/Misc';
import StatusInputEditMode from './StatusInputEditMode';
import { formatIndicatorValue, formatMaybeDate, formatSourceLabel, sortIndicatorsByName } from './utils/statusIndicator.utils';
import { TIndicator } from './status.interfaces';

export interface IStatusInputProps {
  mode: TMode;
  statesList: CStatusIndicator[];
  onChangeList: (list: CStatusIndicator[]) => void;
  title?: string;
  fAllowAdd?: boolean;
  showUpdate?: boolean;
}

const StatusInput = (props: IStatusInputProps) => {
  const { mode, statesList: rawStatesList = [], title, showUpdate } = props;
  const { t } = useTranslation();

  const indicators = useMemo<TIndicator[]>(() => (rawStatesList as TIndicator[]) ?? [], [rawStatesList]);

  const renderUpdate = useCallback((indicator: TIndicator) => {
    const readingTypeId = indicator.readingType?.name ?? (indicator as any).readingTypeId ?? undefined;

    const manual = indicator.update?.manual;
    const live = indicator.update?.live;

    return (
      <div>
        <Checkbox size="small" checked={!!manual} disabled color="primary" label="Manual" />
        <Grid container>
          <Grid size={{ xs: 'auto' }}>
            <Checkbox size="small" checked={!!live} disabled color="primary" label="Live" />
          </Grid>
          <Grid size={{ xs: 'auto' }}>
            {live && readingTypeId && <TextInput disabled label="Reading type" value={String(readingTypeId)} />}
          </Grid>
        </Grid>
      </div>
    );
  }, []);

  const rows = useMemo(() => {
    const sorted = sortIndicatorsByName(indicators as any) as TIndicator[];

    return sorted.map((indicator, idx) => {
      const key = (indicator.logicalId || indicator.id || idx).toString();

      return {
        key,
        name: (indicator as any).name ?? '',
        valueDisplay: formatIndicatorValue(indicator),
        unit: indicator.unit?.abbreviation ?? '',
        lastChangeDisplay: formatMaybeDate((indicator as any).lastChange),
        lastUpdateDisplay: formatMaybeDate((indicator as any).lastUpdate),
        sourceDisplay: formatSourceLabel(indicator.source, t),
        indicator,
      };
    });
  }, [indicators, t]);

  const renderViewMode = () => (
    <Table size="small" sx={{ height: 'fit-content' }}>
      <TableHead>
        <TableRow>
          <TableCell>{t('Indicator')}</TableCell>
          <TableCell>{t('Value')}</TableCell>
          <TableCell>{t('Unit')}</TableCell>
          {showUpdate && <TableCell>{t('Update')}</TableCell>}
          <TableCell>{t('Last change')}</TableCell>
          <TableCell>{t('Last update')}</TableCell>
          <TableCell>{t('Updated by')}</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map(r => (
          <TableRow key={r.key} sx={{ height: 'fit-content' }}>
            <TableCell>{r.name}</TableCell>
            <TableCell>{r.valueDisplay}</TableCell>
            <TableCell>{r.unit}</TableCell>
            {showUpdate && <TableCell>{renderUpdate(r.indicator)}</TableCell>}
            <TableCell>{r.lastChangeDisplay}</TableCell>
            <TableCell>{r.lastUpdateDisplay}</TableCell>
            <TableCell>{r.sourceDisplay}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const content = useMemo(() => {
    switch (mode) {
      case 'view':
        return renderViewMode();

      case 'edit':
      case 'new':
        return <StatusInputEditMode {...props} indicators={indicators as any} renderUpdate={renderUpdate} />;

      default:
        return null;
    }
  }, [mode, props, indicators, renderUpdate]); // renderViewMode está inline (depende de rows)

  return (
    <Grid container alignItems="flex-start">
      {title && <Misc.PaperSectionTitle>{t(title)}</Misc.PaperSectionTitle>}
      {content}
    </Grid>
  );
};

export default StatusInput;
